import React, { useEffect, useState } from "react";
import PageShell from "@/components/layout/PageShell";
import PageHeader from "@/components/common/PageHeader";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { createRequest, getVisaCountries } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

const visaSchema = z.object({
  visa_subtype: z.string().min(1),
  destination_country: z.string().min(1),
  full_name: z.string().min(2),
  email: z.string().email(),
  phone: z.string().min(4).optional(),
  nationality: z.string().optional(),
  residence_country: z.string().optional(),
  travel_dates: z.string().optional(),
  purpose: z.string().optional(),
  passport_number: z.string().optional(),
  passport_expiry: z.string().optional(),
  previous_travel: z.string().optional(),
  funds: z.string().optional(),
  notes: z.string().optional(),
  // extra fields by visa type
  student_institution: z.string().optional(),
  student_course: z.string().optional(),
  student_proof: z.string().optional(),
  skilled_employer: z.string().optional(),
  skilled_job_title: z.string().optional(),
  skilled_contract: z.string().optional(),
  business_company: z.string().optional(),
  business_role: z.string().optional(),
  business_investment: z.string().optional(),
  visitor_accommodation: z.string().optional(),
  visitor_invitation: z.string().optional(),
});

const visaTypes = [
  { value: "student", label: "Student VISA" },
  { value: "skilled_worker", label: "Skilled Worker VISA" },
  { value: "business_immigration", label: "Business Immigration VISA" },
  { value: "visitor", label: "Visitor VISA" },
];

export const VisaPage = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const { toast } = useToast();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);

  const form = useForm({
    resolver: zodResolver(visaSchema),
    defaultValues: {
      visa_subtype: "",
      destination_country: "",
      full_name: "",
      email: "",
      phone: "",
      nationality: "",
      residence_country: "",
      travel_dates: "",
      purpose: "",
      passport_number: "",
      passport_expiry: "",
      previous_travel: "",
      funds: "",
      notes: "",
      student_institution: "",
      student_course: "",
      student_proof: "",
      skilled_employer: "",
      skilled_job_title: "",
      skilled_contract: "",
      business_company: "",
      business_role: "",
      business_investment: "",
      visitor_accommodation: "",
      visitor_invitation: "",
    },
  });

  const visaSubtype = form.watch("visa_subtype");

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const data = await getVisaCountries();
        setCountries(data);
      } catch (err) {
        console.error("Failed to fetch visa countries", err);
      }
    };
    fetchCountries();
  }, []);

  const onSubmit = async (values) => {
    try {
      setLoading(true);
      const payload = {
        language: lang,
        service_type: "visa",
        visa_subtype: values.visa_subtype,
        destination_country: values.destination_country,
        contact: {
          full_name: values.full_name,
          email: values.email,
          phone: values.phone,
          nationality: values.nationality,
          residence_country: values.residence_country,
        },
        meta: {
          travel_dates: values.travel_dates,
          purpose: values.purpose,
          passport_number: values.passport_number,
          passport_expiry: values.passport_expiry,
          previous_travel: values.previous_travel,
          funds: values.funds,
          notes: values.notes,
          student_institution: values.student_institution,
          student_course: values.student_course,
          student_proof: values.student_proof,
          skilled_employer: values.skilled_employer,
          skilled_job_title: values.skilled_job_title,
          skilled_contract: values.skilled_contract,
          business_company: values.business_company,
          business_role: values.business_role,
          business_investment: values.business_investment,
          visitor_accommodation: values.visitor_accommodation,
          visitor_invitation: values.visitor_invitation,
        },
      };
      await createRequest(payload);
      toast({
        title: t("visa.success"),
      });
      form.reset();
    } catch (err) {
      console.error(err);
      toast({
        title: "Erro ao enviar pedido",
        description: "Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const { register, handleSubmit } = form;

  return (
    <PageShell>
      <PageHeader
        title={t("visa.pageTitle")}
        subtitle={t("visa.pageSubtitle")}
        testIdPrefix="visa-page-header"
      />

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid md:grid-cols-[2fr,3fr] gap-8"
        data-testid="visa-form"
      >
        <div className="space-y-6">
          <div>
            <Label
              htmlFor="visa_subtype"
              data-testid="visa-form-type-label"
            >
              {t("visa.visaTypeLabel")}
            </Label>
            <RadioGroup
              className="mt-2 grid gap-2"
              onValueChange={(val) =>
                form.setValue("visa_subtype", val, { shouldValidate: true })
              }
              data-testid="visa-form-type-group"
            >
              {visaTypes.map((type) => (
                <label
                  key={type.value}
                  className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/60 px-3 py-2 text-sm text-slate-100"
                  data-testid={`visa-form-type-${type.value}`}
                >
                  <RadioGroupItem value={type.value} />
                  <span>{type.label}</span>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div>
            <Label
              htmlFor="destination_country"
              data-testid="visa-form-destination-label"
            >
              {t("visa.destinationLabel")}
            </Label>
            <Select
              onValueChange={(val) =>
                form.setValue("destination_country", val, { shouldValidate: true })
              }
            >
              <SelectTrigger
                id="destination_country"
                className="mt-2 bg-slate-900 border-slate-800"
                data-testid="visa-form-destination-select"
              >
                <SelectValue placeholder="Selecione o país de destino" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                {countries.map((c) => (
                  <SelectItem
                    key={c.code}
                    value={c.name}
                    data-testid={`visa-form-destination-option-${c.code}`}
                  >
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Extra fields by visa subtype */}
          {visaSubtype === "student" && (
            <div className="space-y-3" data-testid="visa-form-student-section">
              <p className="text-xs font-semibold text-venka-green-light">
                {t("visa.student.sectionTitle")}
              </p>
              <div>
                <Label htmlFor="student_institution">
                  {t("visa.student.institution")}
                </Label>
                <Input
                  id="student_institution"
                  {...register("student_institution")}
                  className="mt-1 bg-slate-900 border-slate-800"
                  data-testid="visa-form-student-institution-input"
                />
              </div>
              <div>
                <Label htmlFor="student_course">
                  {t("visa.student.course")}
                </Label>
                <Input
                  id="student_course"
                  {...register("student_course")}
                  className="mt-1 bg-slate-900 border-slate-800"
                  data-testid="visa-form-student-course-input"
                />
              </div>
              <div>
                <Label htmlFor="student_proof">
                  {t("visa.student.proof")}
                </Label>
                <Textarea
                  id="student_proof"
                  rows={3}
                  {...register("student_proof")}
                  className="mt-1 bg-slate-900 border-slate-800 resize-none"
                  data-testid="visa-form-student-proof-textarea"
                />
              </div>
            </div>
          )}

          {visaSubtype === "skilled_worker" && (
            <div className="space-y-3" data-testid="visa-form-skilled-section">
              <p className="text-xs font-semibold text-venka-green-light">
                {t("visa.skilled.sectionTitle")}
              </p>
              <div>
                <Label htmlFor="skilled_employer">
                  {t("visa.skilled.employer")}
                </Label>
                <Input
                  id="skilled_employer"
                  {...register("skilled_employer")}
                  className="mt-1 bg-slate-900 border-slate-800"
                  data-testid="visa-form-skilled-employer-input"
                />
              </div>
              <div>
                <Label htmlFor="skilled_job_title">
                  {t("visa.skilled.jobTitle")}
                </Label>
                <Input
                  id="skilled_job_title"
                  {...register("skilled_job_title")}
                  className="mt-1 bg-slate-900 border-slate-800"
                  data-testid="visa-form-skilled-job-title-input"
                />
              </div>
              <div>
                <Label htmlFor="skilled_contract">
                  {t("visa.skilled.contract")}
                </Label>
                <Textarea
                  id="skilled_contract"
                  rows={3}
                  {...register("skilled_contract")}
                  className="mt-1 bg-slate-900 border-slate-800 resize-none"
                  data-testid="visa-form-skilled-contract-textarea"
                />
              </div>
            </div>
          )}

          {visaSubtype === "business_immigration" && (
            <div className="space-y-3" data-testid="visa-form-business-section">
              <p className="text-xs font-semibold text-venka-green-light">
                {t("visa.business.sectionTitle")}
              </p>
              <div>
                <Label htmlFor="business_company">
                  {t("visa.business.company")}
                </Label>
                <Input
                  id="business_company"
                  {...register("business_company")}
                  className="mt-1 bg-slate-900 border-slate-800"
                  data-testid="visa-form-business-company-input"
                />
              </div>
              <div>
                <Label htmlFor="business_role">
                  {t("visa.business.role")}
                </Label>
                <Input
                  id="business_role"
                  {...register("business_role")}
                  className="mt-1 bg-slate-900 border-slate-800"
                  data-testid="visa-form-business-role-input"
                />
              </div>
              <div>
                <Label htmlFor="business_investment">
                  {t("visa.business.investment")}
                </Label>
                <Textarea
                  id="business_investment"
                  rows={3}
                  {...register("business_investment")}
                  className="mt-1 bg-slate-900 border-slate-800 resize-none"
                  data-testid="visa-form-business-investment-textarea"
                />
              </div>
            </div>
          )}

          {visaSubtype === "visitor" && (
            <div className="space-y-3" data-testid="visa-form-visitor-section">
              <p className="text-xs font-semibold text-venka-green-light">
                {t("visa.visitor.sectionTitle")}
              </p>
              <div>
                <Label htmlFor="visitor_accommodation">
                  {t("visa.visitor.accommodation")}
                </Label>
                <Input
                  id="visitor_accommodation"
                  {...register("visitor_accommodation")}
                  className="mt-1 bg-slate-900 border-slate-800"
                  data-testid="visa-form-visitor-accommodation-input"
                />
              </div>
              <div>
                <Label htmlFor="visitor_invitation">
                  {t("visa.visitor.invitation")}
                </Label>
                <Textarea
                  id="visitor_invitation"
                  rows={3}
                  {...register("visitor_invitation")}
                  className="mt-1 bg-slate-900 border-slate-800 resize-none"
                  data-testid="visa-form-visitor-invitation-textarea"
                />
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="full_name">{t("contactInfo.fullName")}</Label>
              <Input
                id="full_name"
                {...register("full_name")}
                className="mt-1 bg-slate-900 border-slate-800"
                data-testid="visa-form-full-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email">{t("contactInfo.email")}</Label>
              <Input
                id="email"
                type="email"
                {...register("email")}
                className="mt-1 bg-slate-900 border-slate-800"
                data-testid="visa-form-email-input"
              />
            </div>
            <div>
              <Label htmlFor="phone">{t("contactInfo.phone")}</Label>
              <Input
                id="phone"
                {...register("phone")}
                className="mt-1 bg-slate-900 border-slate-800"
                data-testid="visa-form-phone-input"
              />
            </div>
            <div>
              <Label htmlFor="nationality">{t("contactInfo.nationality")}</Label>
              <Input
                id="nationality"
                {...register("nationality")}
                className="mt-1 bg-slate-900 border-slate-800"
                data-testid="visa-form-nationality-input"
              />
            </div>
            <div>
              <Label htmlFor="residence_country">
                {t("contactInfo.residenceCountry")}
              </Label>
              <Input
                id="residence_country"
                {...register("residence_country")}
                className="mt-1 bg-slate-900 border-slate-800"
                data-testid="visa-form-residence-country-input"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="travel_dates">{t("visa.travelDates")}</Label>
              <Input
                id="travel_dates"
                {...register("travel_dates")}
                className="mt-1 bg-slate-900 border-slate-800"
                placeholder="Ex: Junho 2025 / 10-20-2025"
                data-testid="visa-form-travel-dates-input"
              />
            </div>
            <div>
              <Label htmlFor="purpose">{t("visa.purpose")}</Label>
              <Input
                id="purpose"
                {...register("purpose")}
                className="mt-1 bg-slate-900 border-slate-800"
                placeholder="Ex: Estudos, Trabalho, Turismo"
                data-testid="visa-form-purpose-input"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="passport_number">{t("visa.passportNumber")}</Label>
              <Input
                id="passport_number"
                {...register("passport_number")}
                className="mt-1 bg-slate-900 border-slate-800"
                data-testid="visa-form-passport-number-input"
              />
            </div>
            <div>
              <Label htmlFor="passport_expiry">{t("visa.passportExpiry")}</Label>
              <Input
                id="passport_expiry"
                {...register("passport_expiry")}
                className="mt-1 bg-slate-900 border-slate-800"
                placeholder="Ex: 12-2028"
                data-testid="visa-form-passport-expiry-input"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="previous_travel">{t("visa.previousTravel")}</Label>
              <Input
                id="previous_travel"
                {...register("previous_travel")}
                className="mt-1 bg-slate-900 border-slate-800"
                placeholder="Ex: Já tive visto para este país em 2022"
                data-testid="visa-form-previous-travel-input"
              />
            </div>
            <div>
              <Label htmlFor="funds">{t("visa.funds")}</Label>
              <Input
                id="funds"
                {...register("funds")}
                className="mt-1 bg-slate-900 border-slate-800"
                placeholder="Ex: Extratos bancários, contrato de trabalho"
                data-testid="visa-form-funds-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="notes">{t("visa.notes")}</Label>
            <Textarea
              id="notes"
              rows={4}
              {...register("notes")}
              className="mt-1 bg-slate-900 border-slate-800 resize-none"
              data-testid="visa-form-notes-textarea"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="rounded-full brand-gradient text-white hover:opacity-90 mt-2"
            data-testid="visa-form-submit-button"
          >
            {loading ? "Enviando..." : t("visa.submit")}
          </Button>
        </div>
      </form>
    </PageShell>
  );
};

export default VisaPage;
